/**
 * Bridges blob URLs (from useAssetUrl / File System Access API) with PixiJS Textures.
 * Manages ref-counted PixiJS Texture instances loaded from blob URLs.
 */
import { Assets, Texture, ImageSource } from 'pixi.js';

interface CacheEntry {
  texture: Texture;
  refCount: number;
}

class PixiTextureCacheImpl {
  private cache = new Map<string, CacheEntry>();
  private loadingPromises = new Map<string, Promise<Texture>>();

  /**
   * Load or retrieve a cached PixiJS Texture from a blob URL.
   * Increments ref count on each call — call releaseTexture() when done.
   */
  async getTexture(blobUrl: string): Promise<Texture> {
    // Return cached texture if available
    const existing = this.cache.get(blobUrl);
    if (existing) {
      existing.refCount++;
      return existing.texture;
    }

    // If already loading, wait for the existing promise
    const loadingPromise = this.loadingPromises.get(blobUrl);
    if (loadingPromise) {
      const texture = await loadingPromise;
      const entry = this.cache.get(blobUrl);
      if (entry) entry.refCount++;
      return texture;
    }

    // Start loading
    const promise = this.loadTexture(blobUrl);
    this.loadingPromises.set(blobUrl, promise);

    try {
      const texture = await promise;
      if (!texture) {
        throw new Error(`Texture load returned null for: ${blobUrl}`);
      }
      this.cache.set(blobUrl, { texture, refCount: 1 });
      return texture;
    } finally {
      this.loadingPromises.delete(blobUrl);
    }
  }

  /**
   * Get a texture synchronously if already cached. Returns null if not loaded.
   * Does NOT increment ref count — use getTexture() for ref-counted access.
   */
  getCachedTexture(blobUrl: string): Texture | null {
    return this.cache.get(blobUrl)?.texture ?? null;
  }

  /**
   * Release a texture reference. When ref count reaches 0, the texture is destroyed.
   */
  releaseTexture(blobUrl: string): void {
    const entry = this.cache.get(blobUrl);
    if (!entry) return;

    entry.refCount--;
    if (entry.refCount <= 0) {
      entry.texture.destroy(true);
      this.cache.delete(blobUrl);
    }
  }

  /**
   * Destroy all cached textures immediately.
   */
  clear(): void {
    for (const [, entry] of this.cache) {
      entry.texture.destroy(true);
    }
    this.cache.clear();
    this.loadingPromises.clear();
  }

  get size(): number {
    return this.cache.size;
  }

  private async loadTexture(blobUrl: string): Promise<Texture> {
    try {
      // Blob URLs have no file extension, so PixiJS can't auto-detect the parser.
      // Explicitly specify the texture parser to bypass the extension-based test().
      const texture = await Assets.load<Texture>({
        src: blobUrl,
        parser: 'loadTextures',
      });
      if (!texture) {
        throw new Error('Assets.load returned null');
      }
      return texture;
    } catch {
      // Fallback: create texture from Image element
      return this.loadTextureViaImage(blobUrl);
    }
  }

  private loadTextureViaImage(blobUrl: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          // Use explicit PixiJS 8 pattern: HTMLImageElement → ImageSource → Texture
          const source = new ImageSource({ resource: img });
          const texture = new Texture({ source });
          resolve(texture);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error(`Failed to load texture from: ${blobUrl}`));
      img.src = blobUrl;
    });
  }
}

/** Singleton texture cache instance */
export const pixiTextureCache = new PixiTextureCacheImpl();
